import { Button, Dropdown, Option, Spinner, Text, Title3 } from '@fluentui/react-components';
import { computeTenantUsage } from '@worklogix/shared';
import type { PlatformUser, SubscriptionTier, TenantDetail, TierKey } from '@worklogix/shared';
import { useCallback, useEffect, useState } from 'react';

import { usePlatformApi } from '../api';
import type { PlatformApi } from '../api';
import { Blade } from '../components/Blade';
import { DataGrid } from '../components/DataGrid';
import type { ResourceColumn } from '../components/DataGrid';
import { EntitlementList } from '../components/EntitlementList';
import { TierBadge } from '../components/TierBadge';
import { UsageMeter } from '../components/UsageMeter';

import { isNotFound, toErrorMessage } from './errors';
import { formatDate } from './format';

/** Discriminated load state for the tenant detail view (header + users + tiers). */
type DetailState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; tenant: TenantDetail; users: PlatformUser[]; tiers: SubscriptionTier[] }
  | { status: 'notFound' }
  | { status: 'error'; message: string };

/**
 * Load a tenant's detail record, its user list, and the tier catalog in
 * parallel. A `null` `tenantId` (blade closed / no selection) resets to idle
 * without fetching. A 404 from the tenant/user calls maps to `notFound`.
 * `refreshToken` re-runs the load (bumped after a successful tier change).
 */
function useTenantDetail(
  api: PlatformApi,
  tenantId: string | null,
  refreshToken: number
): DetailState {
  const [state, setState] = useState<DetailState>({ status: 'idle' });

  useEffect(() => {
    if (tenantId === null) {
      setState({ status: 'idle' });
      return;
    }
    let active = true;
    setState({ status: 'loading' });
    Promise.all([api.getTenant(tenantId), api.listTenantUsers(tenantId), api.getTiers()])
      .then(([tenant, users, tiers]) => {
        if (active) {
          setState({ status: 'ready', tenant, users, tiers });
        }
      })
      .catch((err: unknown) => {
        if (!active) {
          return;
        }
        setState(
          isNotFound(err)
            ? { status: 'notFound' }
            : { status: 'error', message: toErrorMessage(err) }
        );
      });
    return () => {
      active = false;
    };
  }, [api, tenantId, refreshToken]);

  return state;
}

/** Users grid columns for the tenant detail blade. */
const userColumns: ResourceColumn<PlatformUser>[] = [
  { columnId: 'email', header: 'Email', renderCell: (u) => u.email ?? '—' },
  { columnId: 'externalId', header: 'External ID', renderCell: (u) => u.externalId },
  { columnId: 'created', header: 'Created', renderCell: (u) => formatDate(u.createdAt) },
];

interface TenantDetailReadyProps {
  api: PlatformApi;
  tenant: TenantDetail;
  users: PlatformUser[];
  tiers: SubscriptionTier[];
  /** Re-run the detail load (e.g. after a tier change persists). */
  onReload: () => void;
}

/**
 * Ready-state body: header facts (slug, tier, created), a soft-warn usage meter,
 * the tier's entitlements, a change-tier control, and the users grid.
 *
 * Soft-warn model: nothing here blocks. A downgrade below current usage is
 * permitted by the server; after it persists we simply reload and the usage
 * meter renders in its over-limit (amber) state. All controls stay enabled.
 */
function TenantDetailReady({
  api,
  tenant,
  users,
  tiers,
  onReload,
}: TenantDetailReadyProps): JSX.Element {
  const [selectedTier, setSelectedTier] = useState<TierKey>(tenant.subscriptionTier);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const usage = computeTenantUsage(tenant.userCount, tenant.maxUsers);
  const currentTier = tiers.find((t) => t.key === tenant.subscriptionTier);
  const selectedTierName = tiers.find((t) => t.key === selectedTier)?.name ?? selectedTier;
  const dirty = selectedTier !== tenant.subscriptionTier;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.setTenantTier(tenant.id, selectedTier);
      onReload();
    } catch (err: unknown) {
      setSaveError(toErrorMessage(err));
      setSaving(false);
    }
  }, [api, tenant.id, selectedTier, onReload]);

  return (
    <div className="wlx-tenant-detail">
      <dl className="wlx-detail-facts">
        <div className="wlx-detail-fact">
          <dt>Slug</dt>
          <dd>{tenant.slug}</dd>
        </div>
        <div className="wlx-detail-fact">
          <dt>Tier</dt>
          <dd>
            <TierBadge tier={tenant.subscriptionTier} />
          </dd>
        </div>
        <div className="wlx-detail-fact">
          <dt>Created</dt>
          <dd>{formatDate(tenant.createdAt)}</dd>
        </div>
        <div className="wlx-detail-fact">
          <dt>Usage</dt>
          <dd>
            <UsageMeter usage={usage} />
          </dd>
        </div>
      </dl>

      {currentTier ? (
        <>
          <Title3 as="h3" className="wlx-detail-section-title">
            Entitlements
          </Title3>
          <EntitlementList features={currentTier.features} />
        </>
      ) : null}

      <Title3 as="h3" className="wlx-detail-section-title">
        Change tier
      </Title3>
      <div className="wlx-tier-change">
        <Dropdown
          aria-label="Subscription tier"
          value={selectedTierName}
          selectedOptions={[selectedTier]}
          disabled={saving || tiers.length === 0}
          onOptionSelect={(_, data) => {
            const picked = tiers.find((t) => t.key === data.optionValue);
            if (picked) {
              setSelectedTier(picked.key);
            }
          }}
        >
          {tiers.map((t) => (
            <Option key={t.key} value={t.key} text={t.name}>
              {t.name}
            </Option>
          ))}
        </Dropdown>
        <Button appearance="primary" disabled={!dirty || saving} onClick={() => void handleSave()}>
          {saving ? <Spinner size="tiny" /> : 'Save'}
        </Button>
      </div>
      {saveError ? (
        <Text role="alert" className="wlx-form-error">
          {saveError}
        </Text>
      ) : null}

      <Title3 as="h3" className="wlx-detail-section-title">
        Users
      </Title3>
      {users.length === 0 ? (
        <Text className="wlx-detail-empty">No users provisioned for this tenant yet.</Text>
      ) : (
        <DataGrid columns={userColumns} rows={users} getRowId={(u) => u.id} />
      )}
    </div>
  );
}

function renderDetail(state: DetailState, api: PlatformApi, onReload: () => void): JSX.Element {
  if (state.status === 'idle' || state.status === 'loading') {
    return <Spinner label="Loading tenant…" />;
  }
  if (state.status === 'notFound') {
    return (
      <Text role="alert" className="wlx-detail-error">
        That tenant could not be found. It may have been removed.
      </Text>
    );
  }
  if (state.status === 'error') {
    return (
      <Text role="alert" className="wlx-detail-error">
        {state.message}
      </Text>
    );
  }

  return (
    <TenantDetailReady
      api={api}
      tenant={state.tenant}
      users={state.users}
      tiers={state.tiers}
      onReload={onReload}
    />
  );
}

export interface TenantDetailBladeProps {
  /** Whether the detail blade is open. */
  open: boolean;
  /** Tenant to display; `null` shows nothing / resets the loader. */
  tenantId: string | null;
  /** Close the blade. */
  onClose: () => void;
}

/**
 * Right-sliding detail blade for a single tenant: header facts (slug, tier,
 * created) with a soft-warn usage meter, the tier's entitlements, a change-tier
 * control, and a users grid. Handles loading, empty-users, 404, and error
 * states. Data loads only while the blade is open and a tenant is selected.
 */
export function TenantDetailBlade({
  open,
  tenantId,
  onClose,
}: TenantDetailBladeProps): JSX.Element {
  const api = usePlatformApi();
  const [refreshToken, setRefreshToken] = useState(0);
  const reload = useCallback(() => setRefreshToken((n) => n + 1), []);
  const state = useTenantDetail(api, open ? tenantId : null, refreshToken);
  const title = state.status === 'ready' ? state.tenant.name : 'Tenant';

  return (
    <Blade open={open} onClose={onClose} title={title}>
      {renderDetail(state, api, reload)}
    </Blade>
  );
}
